import { PayloadAction } from '@reduxjs/toolkit';
import {
  DeleteRequestedItemPayload,
  UpdateRequestedItemPayload,
} from '@/domains/milestones/types/RequestedItemPayloads';
import { call, put, takeLatest } from 'redux-saga/effects';
import { Modals } from '@/features/serviceModal/enums/Modals';
import { SnackType } from '@/features/snacks/enums/SnackType';
import { AddRequestedItemFormFields } from '@/domains/milestones/components/AddRequestedItemModal/AddRequestedItemForm/addRequestedItemForm.config';
import { RequestedItemEvents } from '@/domains/milestones/events';
import {
  addRequestedItem,
  addRequestedItemSuccess,
  deleteRequestedItem,
  deleteRequestedItemSuccess,
  updateRequestedItem,
  updateRequestedItemSuccess,
} from '@/domains/milestones/store/milestonesSlice';
import EventService from '@/services/EventService/EventService';
import HttpService from '@/services/HttpService/HttpService';
import ModalService from '@/services/ModalService/ModalService';
import SnacksService from '@/services/SnackService/SnackService';

function* addRequestedItemSaga({ payload }: PayloadAction<AddRequestedItemFormFields>) {
  try {
    yield call(
      HttpService.post,
      '/examiner/requested-items/create',
      {
        ...payload,
        description: payload.description || undefined,
      },
      {
        baseURL: import.meta.env.VITE_API_URL_V2,
      },
    );

    yield put(addRequestedItemSuccess());

    EventService.dispatch(new RequestedItemEvents.Added({}));

    SnacksService.add({
      type: SnackType.Success,
      title: 'Requested item added.',
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });
  } catch (error) {
    console.error(error);

    SnacksService.add({
      type: SnackType.Error,
      title: 'Failed to save the updates. Please try again.',
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });
  } finally {
    ModalService.close(Modals.AddRequestedItem);
  }
}

function* updateRequestedItemSaga({ payload }: PayloadAction<UpdateRequestedItemPayload>) {
  try {
    yield call(
      HttpService.put,
      '/examiner/requested-items/update',
      {
        ...payload,
      },
      {
        baseURL: import.meta.env.VITE_API_URL_V2,
      },
    );

    yield put(updateRequestedItemSuccess());
    EventService.dispatch(new RequestedItemEvents.Updated({}));

    SnacksService.add({
      type: SnackType.Success,
      title: 'Requested item updated.',
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });
  } catch (error) {
    console.error(error);

    SnacksService.add({
      type: SnackType.Error,
      title: 'Failed to save the updates. Please try again.',
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });
  } finally {
    ModalService.close(Modals.UpdateRequestedItem);
  }
}

function* deleteRequestedItemSaga({ payload }: PayloadAction<DeleteRequestedItemPayload>) {
  try {
    yield call(HttpService.delete, `/examiner/requested-items/delete/${payload.requestedItemId}`, {
      baseURL: import.meta.env.VITE_API_URL_V2,
    });
    yield put(deleteRequestedItemSuccess());
    EventService.dispatch(new RequestedItemEvents.Deleted({}));

    SnacksService.add({
      type: SnackType.Success,
      title: 'Requested item deleted.',
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });
  } catch (error) {
    console.error(error);

    SnacksService.add({
      type: SnackType.Error,
      title: 'Failed to save the updates. Please try again.',
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });
  } finally {
    ModalService.close(Modals.DeleteRequestedItem);
  }
}

export function* requestedItemsWatcher() {
  yield takeLatest([addRequestedItem.type], addRequestedItemSaga);
  yield takeLatest([deleteRequestedItem.type], deleteRequestedItemSaga);
  yield takeLatest([updateRequestedItem.type], updateRequestedItemSaga);
}
